/**
 * ChoosePathStep - Step 3: Choose between sample data or connecting own data.
 */
import React, { useState } from 'react';
import {
  Box, Card, CardActionArea, Typography, Stack, useTheme, alpha,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import BoltRounded from '@mui/icons-material/BoltRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { brandPalette } from '../../../theme/brandPalette';
import OnboardingLayout from '../components/OnboardingLayout';

type PathChoice = 'sample' | 'own_data';

interface ChoosePathStepProps {
  onChoose: (path: PathChoice) => Promise<any>;
  /** Initial highlighted card (e.g. own_data after user backs out of sample flow). */
  defaultSelectedPath?: PathChoice;
  loading: boolean;
  error: string | null;
}

const paths = [
  {
    id: 'sample' as PathChoice,
    emoji: '⚡',
    Icon: BoltRounded,
    badge: 'Fastest to start',
    title: 'Explore a demo dataset',
    description: 'Explore a fully loaded dataset - charts, insights and all. No setup needed.',
    outcome: "You'll be asking questions in 30 seconds.",
  },
  {
    id: 'own_data' as PathChoice,
    emoji: '🗄️',
    Icon: StorageRounded,
    badge: 'Highest value',
    title: 'Connect your data',
    description: 'Snowflake, BigQuery, Redshift, Postgres, MySQL - we connect to all of them.',
    outcome: "You'll have answers from your real data in under 5 minutes.",
  },
];

export default function ChoosePathStep({
  onChoose,
  defaultSelectedPath = 'sample',
  loading,
  error,
}: ChoosePathStepProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [selected, setSelected] = useState<PathChoice>(defaultSelectedPath);

  return (
    <OnboardingLayout maxWidth={580} stepLabel="Step 3 of 3">
      <Box sx={{ textAlign: 'center', mb: 3.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, letterSpacing: -0.4 }}>
          How would you like to start?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Pick a path - you can always switch later.
        </Typography>
      </Box>

      {/* Two vertical square cards side by side */}
      <Stack direction="row" spacing={2} sx={{ mb: 3.5 }}>
        {paths.map((path) => {
          const isSelected = selected === path.id;
          return (
            <Card
              key={path.id}
              sx={{
                flex: 1,
                borderRadius: 2,
                border: '1px solid',
                borderColor: isSelected
                  ? alpha(brandPalette.primary[300], 0.65)
                  : isDark ? alpha('#fff', 0.1) : brandPalette.neutral[300],
                bgcolor: isSelected
                  ? (isDark ? alpha(brandPalette.primary[400], 0.06) : alpha(brandPalette.primary[100], 0.35))
                  : theme.palette.background.paper,
                transition: 'all 0.15s ease',
                boxShadow: isSelected
                  ? `0 1px 2px ${alpha(brandPalette.neutral[800], 0.06)}, 0 0 0 1px ${alpha(brandPalette.primary[300], 0.25)}`
                  : `0 1px 2px ${alpha(brandPalette.neutral[800], 0.04)}`,
                '&:hover': {
                  borderColor: isSelected ? alpha(brandPalette.primary[300], 0.85) : brandPalette.neutral[400],
                },
              }}
            >
              <CardActionArea
                onClick={() => setSelected(path.id)}
                sx={{ p: 0, height: '100%', alignItems: 'flex-start' }}
              >
                <Box sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>

                  {/* Top row: icon box + checkmark */}
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Box sx={{
                      width: 48, height: 48, borderRadius: 1.5,
                      bgcolor: isSelected
                        ? alpha(brandPalette.primary[400], 0.2)
                        : isDark ? alpha('#fff', 0.06) : brandPalette.neutral[200],
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s',
                    }}>
                      <path.Icon sx={{
                        fontSize: 24,
                        color: isSelected ? brandPalette.primary[500] : brandPalette.neutral[600],
                      }} />
                    </Box>
                    <CheckCircleRounded sx={{
                      fontSize: 22,
                      color: isSelected ? brandPalette.primary[400] : isDark ? alpha('#fff', 0.15) : brandPalette.neutral[400],
                      transition: 'color 0.15s',
                    }} />
                  </Stack>

                  {/* Badge */}
                  <Box sx={{
                    display: 'inline-flex', alignSelf: 'flex-start',
                    px: 1, py: 0.25,
                    borderRadius: 1,
                    bgcolor: isSelected
                      ? alpha(brandPalette.primary[300], 0.14)
                      : isDark ? alpha('#fff', 0.06) : brandPalette.neutral[200],
                    mb: 1,
                  }}>
                    <Typography sx={{
                      fontSize: '0.65rem', fontWeight: 700,
                      letterSpacing: 0.5, textTransform: 'uppercase',
                      color: isSelected ? brandPalette.primary[500] : 'text.secondary',
                    }}>
                      {path.badge}
                    </Typography>
                  </Box>

                  {/* Title */}
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.75, lineHeight: 1.3, color: 'text.primary' }}>
                    {path.title}
                  </Typography>

                  {/* Description */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5, flexGrow: 1 }}>
                    {path.description}
                  </Typography>

                  {/* Outcome line */}
                  <Typography sx={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    color: isSelected ? brandPalette.neutral[700] : 'text.disabled',
                    fontStyle: 'italic',
                    transition: 'color 0.15s',
                  }}>
                    {path.outcome}
                  </Typography>

                </Box>
              </CardActionArea>
            </Card>
          );
        })}
      </Stack>

      <LoadingButton
        variant="contained"
        color="primary"
        fullWidth
        loading={loading}
        onClick={() => onChoose(selected)}
        size="large"
        endIcon={<ArrowForwardRounded />}
        sx={{
          py: 1.5, borderRadius: 2, fontWeight: 600,
          textTransform: 'none',
          bgcolor: brandPalette.primary[400],
          boxShadow: `0 1px 2px ${alpha(brandPalette.neutral[800], 0.08)}`,
          '&:hover': { bgcolor: brandPalette.primary[500] },
        }}
      >
        {selected === 'sample' ? 'Start with demo data' : 'Connect my database'}
      </LoadingButton>
    </OnboardingLayout>
  );
}
